import { AddOutlined, DeleteOutlined, EditOutlined, GroupOutlined } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { generateClient, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { PageAppBar } from 'src/components/pageAppBar';
import { UserContext } from 'src/contexts/UserContext';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from 'src/theme';
import { notificationGroupsByTenant } from '../../graphql/queries';
import { createNotificationGroup, updateNotificationGroup, deleteNotificationGroup } from '../../graphql/mutations';
import { fetchAllPages } from 'src/tools/graphqlPaging';

export function NotificationGroups() {
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupName, setGroupName] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(null);

    useEffect(() => {
        if (userContext.tenantId) {
            loadGroups();
            loadUsers();
        }
    }, [userContext.tenantId]);

    async function loadGroups() {
        try {
            setGroups(
                await fetchAllPages({
                    client,
                    query: notificationGroupsByTenant,
                    resultKey: 'notificationGroupsByTenant',
                    variables: { tenant: userContext.tenantId },
                }),
            );
        } catch (err) {
            console.error('Error loading notification groups', err);
        }
    }

    async function loadUsers() {
        try {
            const userResponse = await get({
                apiName: 'cdyxoutreach',
                path: '/admin/list-users',
                options: {
                    headers: {
                        Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
                        'x-api-key': userContext.apiKey,
                    },
                },
            }).response;

            const userResults = await userResponse.body.json();

            // Filter out users who only have liveAgentSelect security
            const eligibleUsers = userResults.filter(user => {
                if (!user.securityOptions) return false;
                const opts = user.securityOptions.split('|');
                // If ONLY liveAgentSelect (index 6) is non-zero, exclude the user
                const hasOtherPermission = opts.some((val, idx) => idx !== 6 && val !== '0');
                return hasOtherPermission;
            });

            setUsers(eligibleUsers);
        } catch (err) {
            console.error('Error loading users', err);
        }
    }

    function openCreateDialog() {
        setEditingGroup(null);
        setGroupName('');
        setSelectedMembers([]);
        setDialogOpen(true);
    }

    function openEditDialog(group) {
        setEditingGroup(group);
        setGroupName(group.name);
        setSelectedMembers(group.members.map(m => users.find(u => u.id === m.cognitoId) || { id: m.cognitoId, name: m.name, email: m.email }));
        setDialogOpen(true);
    }

    async function handleSave() {
        if (!groupName.trim()) {
            enqueueSnackbar('Group name is required', { variant: 'warning' });
            return;
        }
        if (selectedMembers.length === 0) {
            enqueueSnackbar('At least one member is required', { variant: 'warning' });
            return;
        }

        const members = selectedMembers.map(user => ({
            cognitoId: user.id,
            name: user.name,
            email: user.email,
        }));

        try {
            if (editingGroup) {
                await client.graphql({
                    query: updateNotificationGroup,
                    variables: {
                        input: {
                            id: editingGroup.id,
                            name: groupName.trim(),
                            members,
                        },
                    },
                });
                enqueueSnackbar('Notification group updated', { variant: 'success' });
            } else {
                await client.graphql({
                    query: createNotificationGroup,
                    variables: {
                        input: {
                            tenant: userContext.tenantId,
                            name: groupName.trim(),
                            members,
                        },
                    },
                });
                enqueueSnackbar('Notification group created', { variant: 'success' });
            }
            setDialogOpen(false);
            loadGroups();
        } catch (err) {
            console.error('Error saving notification group', err);
            enqueueSnackbar('Error saving notification group', { variant: 'error' });
        }
    }

    async function handleDelete(group) {
        try {
            await client.graphql({
                query: deleteNotificationGroup,
                variables: { input: { id: group.id } },
            });
            enqueueSnackbar('Notification group deleted', { variant: 'success' });
            setConfirmDelete(null);
            loadGroups();
        } catch (err) {
            console.error('Error deleting notification group', err);
            enqueueSnackbar('Error deleting notification group', { variant: 'error' });
        }
    }

    return (
        <Box>
            <PageAppBar
                title="Notification Groups"
                description="Create groups of users to notify when imports and other operations complete."
                actionOneText={userContext.securityOptions.userManagement > 1 ? '+ Group' : ''}
                actionOneHandler={openCreateDialog}
                actionOneDisabled={userContext.securityOptions.userManagement < 2}
            />

            <Box sx={{ p: 2 }}>
                {groups.length === 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                            <GroupOutlined sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="h6" color="text.secondary">No notification groups yet</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Create a group to notify multiple users when imports or operations complete.
                            </Typography>
                            {userContext.securityOptions.userManagement > 1 && (
                                <Button sx={actionOneButtonStyle} onClick={openCreateDialog}>
                                    Create Your First Group
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={3}>
                    {groups.map(group => (
                        <Card key={group.id} variant="outlined" sx={cardStyle}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="h6">{group.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
                                        </Typography>
                                    </Box>
                                    {userContext.securityOptions.userManagement > 1 && (
                                        <Box>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEditDialog(group)}>
                                                    <EditOutlined fontSize="small" color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => setConfirmDelete(group)}>
                                                    <DeleteOutlined fontSize="small" color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                                    {group.members.map(member => (
                                        <Chip
                                            key={member.cognitoId}
                                            label={member.name}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingGroup ? 'Edit Notification Group' : 'Create Notification Group'}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Group Name"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            fullWidth
                            placeholder="e.g. Import Team, Operations"
                        />
                        <Autocomplete
                            multiple
                            options={users}
                            value={selectedMembers}
                            onChange={(e, newValue) => setSelectedMembers(newValue)}
                            getOptionLabel={(option) => option.name || option.email}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Members" placeholder="Search users..." />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.name || option.email}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        {...getTagProps({ index })}
                                        key={option.id}
                                    />
                                ))
                            }
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Box>
                                        <Typography variant="body1">{option.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">{option.email}</Typography>
                                    </Box>
                                </li>
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button sx={actionTwoButtonStyle} onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button sx={actionOneButtonStyle} onClick={handleSave}>
                        {editingGroup ? 'Save Changes' : 'Create Group'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete Notification Group</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{confirmDelete?.name}"? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button sx={actionTwoButtonStyle} onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
